import React from 'react';
import { Phone, Mail, MapPin, MessageCircle, Clock } from 'lucide-react';
import { brandInfo } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

export const Contact = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-red-600 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-red-100">Available 24/7 for emergencies and patient transport</p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Emergency Call Cards */}
            <Card className="bg-red-600 text-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-8 h-8 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3">Emergency Hotline</h3>
                    <div className="space-y-1">
                      <a
                        href={`tel:${brandInfo.phones[0]}`}
                        className="block text-2xl font-bold hover:text-red-100 transition-colors"
                      >
                        {brandInfo.phones[0]}
                      </a>
                      <a
                        href={`tel:${brandInfo.phones[1]}`}
                        className="block text-2xl font-bold hover:text-red-100 transition-colors"
                      >
                        {brandInfo.phones[1]}
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp Card */}
            <Card className="bg-green-500 text-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-3">WhatsApp</h3>
                    <Button
                      asChild
                      size="lg"
                      variant="secondary"
                      className="w-full bg-white text-green-600 hover:bg-gray-100 font-semibold"
                    >
                      <a
                        href={`https://wa.me/${brandInfo.whatsappNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Chat with us on WhatsApp
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-red-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2 text-lg">Email</h4>
                <a
                  href={`mailto:${brandInfo.email}`}
                  className="text-gray-600 hover:text-red-600 transition-colors break-all text-sm"
                >
                  {brandInfo.email}
                </a>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-red-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2 text-lg">Address</h4>
                <p className="text-gray-600 text-sm">{brandInfo.address}</p>
              </CardContent>
            </Card>

            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-7 h-7 text-red-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2 text-lg">Availability</h4>
                <p className="text-gray-600 text-sm">24 Hours, 7 Days a Week</p>
              </CardContent>
            </Card>
          </div>

          {/* Map Section */}
          <Card className="shadow-lg overflow-hidden">
            <iframe
              src={brandInfo.mapEmbed}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Care Ambulance Service Location"
            ></iframe>
          </Card>
          <div className="mt-4 text-center">
            <Button asChild variant="outline" size="lg" className="border-red-600 text-red-600 hover:bg-red-50">
              <a href={brandInfo.mapLink} target="_blank" rel="noopener noreferrer">
                Open in Google Maps
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};